import { useParams } from 'react-router-dom'
import { useToast } from '@chakra-ui/react'
import {
  InfiniteData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'

import {
  AddCustomSummaryPromptRequest,
  CreateInterviewRequest,
  GetCustomSummaryPromptsResponse,
  GetInterviewFilterOptionsResponse,
  GetInterviewResponse,
  GetRoleResponse,
  GetShortInterviewsRequestParams,
  GetTeamCustomSummaryPromptsResponse,
  UpdateCustomSummaryPromptRequest,
  UpdateInterviewRequest,
  UpdateSummaryPartRequest,
  UpdateSummaryPartScoreResponse,
} from '@pkg/types/api'
import { SummaryPart } from '@pkg/types/interview'

import api from '@/api'
import { CANDIDATE_KEY } from '@/react-query/candidate'
import {
  LIST_LAST_CUSTOM_SUMMARY_PROMPTS_KEY,
  LIST_TEAM_CUSTOM_SUMMARY_PROMPTS_KEY,
} from '@/react-query/custom-summary-parts'
import { ROLE_KEY } from '@/react-query/role'
import {
  deleteAudioChunks,
  getAudioChunks,
} from '@/views/screens/Interview/context/AudioRecorderContext/indexed-db'

import { COMPANY_KEY } from '../company'
import { useUser } from '../user'

export const INTERVIEW_KEY = 'interview'
export const SHORT_INTERVIEWS_LIST_KEY = 'shortInterviewsList'

export const INTERVIEW_FILTER_OPTIONS_KEY = 'interviewFilterOptions'
export const INTERVIEW_FILTER_OPTIONS_INTAKE_KEY =
  'interviewFilterOptionsIntake'

export const useInterview = () => {
  const queryClient = useQueryClient()
  const interviewId = useParams().interviewId || ''

  return useQuery({
    queryKey: [INTERVIEW_KEY, interviewId],
    queryFn: async () => {
      if (!interviewId) {
        return null
      }
      const data = (await api.getInterview(interviewId)).data

      queryClient.setQueryData(
        [ROLE_KEY, data.interview.roleId],
        data.interview.Role,
      )
      queryClient.setQueryData(
        [CANDIDATE_KEY, data.interview.candidateId],
        data.interview.Candidate,
      )
      queryClient.setQueryData(
        [COMPANY_KEY, data.interview.companyId],
        data.interview.Company,
      )
      if (data.interview.fullTranscript) {
        await deleteAudioChunks(interviewId)
      }
      return data.interview
    },
    staleTime: 1000 * 20, // 20 seconds
    enabled: !!interviewId,
  })
}

export const useCreateInterview = () => {
  const queryClient = useQueryClient()
  const user = useUser()

  return useMutation({
    mutationFn: async (interview: CreateInterviewRequest['interview']) => {
      const response = await api.createInterview({
        interview: {
          ...interview,
          dateTimestamp: new Date(),
        },
      })
      // Refresh database data to get the latest credits.
      user.refetch().catch(console.error)

      queryClient.setQueryData(
        [INTERVIEW_KEY, response.data.interview.id],
        response.data.interview,
      )
      queryClient.invalidateQueries({
        queryKey: [SHORT_INTERVIEWS_LIST_KEY],
      })

      return response.data.interview
    },
    onError: (error) => {
      console.log('got error in useCreateInterview', error)
    },
  })
}

// useUpdateInterview is a custom hook that uses useMutation to update or create an interview.
// Before interview is created, frontend will treat interview as with interviewId = ''.
export const useUpdateInterview = () => {
  const queryClient = useQueryClient()
  const { data: user } = useUser()

  const interviewId = useParams().interviewId

  return useMutation({
    mutationFn: async (interview: UpdateInterviewRequest['interview']) => {
      if (!interviewId) {
        return
      }

      const response = await api.updateInterview(interviewId, {
        interview: interview as UpdateInterviewRequest['interview'],
      })

      return response.data.interview
    },
    onMutate: (interview) => {
      queryClient.setQueryData(
        [INTERVIEW_KEY, interviewId],
        (oldData?: GetInterviewResponse['interview']) => {
          let FullTranscriptEditedBy = oldData?.FullTranscriptEditedBy
          if (
            interview.fullTranscript !== undefined &&
            oldData?.fullTranscript !== interview.fullTranscript &&
            user?.email
          ) {
            FullTranscriptEditedBy = {
              email: user.email,
              displayName: user.displayName || '',
              photoURL: user.photoURL || '',
            }
          }
          return {
            ...oldData,
            ...interview,
            ...(FullTranscriptEditedBy ? { FullTranscriptEditedBy } : {}),
          }
        },
      )
    },
    onError: () => {
      return queryClient.invalidateQueries({
        queryKey: [INTERVIEW_KEY, interviewId],
      })
    },
    scope: {
      id: interviewId || '',
    },
  })
}

export const useDeleteInterview = (id?: string) => {
  const queryClient = useQueryClient()
  const paramsInterviewId = useParams().interviewId
  const interviewId = id || paramsInterviewId

  return useMutation({
    mutationFn: async (reason: string = 'manual') => {
      if (!interviewId) {
        return
      }
      await deleteAudioChunks(interviewId)
      await api.deleteInterview(interviewId, reason)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [SHORT_INTERVIEWS_LIST_KEY],
      })

      queryClient.setQueryData([INTERVIEW_KEY, interviewId], undefined)
    },
  })
}

export const useRegenerateShortTranscript = () => {
  const queryClient = useQueryClient()
  const interviewId = useInterview().data?.id

  return useMutation({
    mutationFn: async () => {
      if (!interviewId) {
        return null
      }
      const response = await api.regenerateShortTranscript(interviewId)
      return response.data.shortInterview
    },
    onSuccess: (data) => {
      queryClient.setQueryData(
        [INTERVIEW_KEY, interviewId],
        (
          oldData: GetInterviewResponse['interview'],
        ): GetInterviewResponse['interview'] => ({
          ...oldData,
          shortTranscript: data,
        }),
      )
    },
  })
}

export const useRegenerateSummary = () => {
  const queryClient = useQueryClient()
  const interview = useInterview().data
  const toast = useToast()

  return useMutation({
    mutationFn: async () => {
      if (!interview?.id) {
        return null
      }
      const response = await api.regenerateInterviewSummary(interview.id)
      return response.data
    },
    onSuccess: (data) => {
      queryClient.setQueryData(
        [INTERVIEW_KEY, interview?.id],
        (
          oldData: GetInterviewResponse['interview'],
        ): GetInterviewResponse['interview'] => ({
          ...oldData,
          SummaryParts: data?.summaryParts,
          summaryLanguage: data?.summaryLanguage,
        }),
      )
      queryClient.setQueryData(
        [ROLE_KEY, interview?.roleId],
        (oldData: GetRoleResponse) => ({
          ...oldData,
          summaryLanguage: data?.summaryLanguage,
        }),
      )
    },
    onError: () => {
      toast({
        title: 'Failed to generate scorecard',
        description:
          'Something went wrong while generating the scorecard. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    },
  })
}

export const useGenerateSummaryPart = () => {
  const queryClient = useQueryClient()
  const interview = useInterview().data
  const toast = useToast()

  return useMutation({
    mutationFn: async (data: {
      partName?: string
      customSummaryPartPromptId?: string
    }) => {
      if (!interview?.id) {
        return null
      }
      const response = await api.generateSummaryPart(interview.id, data)
      return response.data
    },
    onSuccess: (data) => {
      if (!data?.summaryPart) return

      queryClient.setQueryData(
        [INTERVIEW_KEY, interview?.id],
        (
          oldData: GetInterviewResponse['interview'],
        ): GetInterviewResponse['interview'] => {
          const existingParts = oldData.SummaryParts || []
          const newPart = data.summaryPart

          const existingIndex = existingParts.findIndex(
            (p) => p.id === newPart.id,
          )

          let updatedParts: typeof existingParts
          if (existingIndex >= 0) {
            updatedParts = [...existingParts]
            updatedParts[existingIndex] = newPart
          } else {
            updatedParts = [...existingParts, newPart].sort(
              (a, b) => a.order - b.order,
            )
          }

          return {
            ...oldData,
            SummaryParts: updatedParts,
          }
        },
      )
    },
    onError: () => {
      toast({
        title: 'Failed to generate part',
        description:
          'Something went wrong while generating this scorecard part. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    },
  })
}

export const useAddCustomSummaryPrompt = () => {
  const queryClient = useQueryClient()
  const interviewId = useInterview().data?.id

  return useMutation({
    mutationFn: async (data: AddCustomSummaryPromptRequest) => {
      if (!interviewId) {
        return null
      }
      const response = await api.addCustomSummaryPartPrompt(interviewId, data)
      return response.data
    },
    onSuccess: (data) => {
      queryClient.setQueryData(
        [INTERVIEW_KEY, interviewId],
        (
          oldData: GetInterviewResponse['interview'],
        ): GetInterviewResponse['interview'] => ({
          ...oldData,
          SummaryParts: data?.summaryParts,
        }),
      )
      // Invalidate custom summary prompts queries to refresh the lists
      queryClient.invalidateQueries({
        queryKey: [LIST_LAST_CUSTOM_SUMMARY_PROMPTS_KEY],
      })
      queryClient.invalidateQueries({
        queryKey: [LIST_TEAM_CUSTOM_SUMMARY_PROMPTS_KEY],
      })
    },
  })
}

export const useEditCustomSummaryPrompt = () => {
  const queryClient = useQueryClient()
  const interviewId = useInterview().data?.id

  return useMutation({
    mutationFn: async (data: {
      partId: string
      data: UpdateCustomSummaryPromptRequest
    }) => {
      const response = await api.updateCustomSummaryPartPrompt(
        data.partId,
        data.data,
      )
      return response.data
    },
    onSuccess: (data) => {
      queryClient.setQueryData(
        [INTERVIEW_KEY, interviewId],
        (
          oldData: GetInterviewResponse['interview'],
        ): GetInterviewResponse['interview'] => {
          const newSummaryParts = [...(oldData?.SummaryParts || [])].map(
            (part) => {
              if (part.id === data.summaryPart.id) {
                return {
                  ...part,
                  ...data.summaryPart,
                } as SummaryPart
              }
              return part
            },
          )
          return {
            ...oldData,
            SummaryParts: newSummaryParts,
          }
        },
      )
    },
  })
}

export const useUpdateSummaryParts = () => {
  const queryClient = useQueryClient()
  const interviewId = useInterview().data?.id

  return useMutation({
    mutationFn: async (data: UpdateSummaryPartRequest) => {
      const response = await api.updateSummaryParts(data)
      return response.data
    },
    onMutate: (data) => {
      queryClient.setQueryData(
        [INTERVIEW_KEY, interviewId],
        (
          oldData: GetInterviewResponse['interview'],
        ): GetInterviewResponse['interview'] => {
          const newSummaryParts = [...(oldData?.SummaryParts || [])].map(
            (part) => {
              const updatedPart = data.summaryParts.find(
                (updatedPart) => updatedPart.id === part.id,
              )
              if (updatedPart) {
                return {
                  ...part,
                  ...updatedPart,
                }
              }
              return part
            },
          )
          return {
            ...oldData,
            SummaryParts: newSummaryParts,
          }
        },
      )
    },
    onError: (error) => {
      console.log('error', error)
      return queryClient.invalidateQueries({
        queryKey: [INTERVIEW_KEY, interviewId],
      })
    },
  })
}

export const useDeleteSummaryPart = () => {
  const queryClient = useQueryClient()
  const interviewId = useInterview().data?.id

  return useMutation({
    mutationFn: async (partId: string) => {
      if (!interviewId) {
        return null
      }
      await api.deleteSummaryPart({ partId })
    },
    onMutate: async (partId: string) => {
      await queryClient.cancelQueries({
        queryKey: [INTERVIEW_KEY, interviewId],
      })

      const previousInterviewData = queryClient.getQueryData<
        GetInterviewResponse['interview']
      >([INTERVIEW_KEY, interviewId])

      queryClient.setQueryData(
        [INTERVIEW_KEY, interviewId],
        (oldData?: GetInterviewResponse['interview']) => {
          if (!oldData) return oldData

          const newSummaryParts = oldData.SummaryParts?.filter(
            (part) => part.id !== partId,
          ).map((part, index) => ({
            ...part,
            order: index,
          }))

          return {
            ...oldData,
            SummaryParts: newSummaryParts,
          }
        },
      )

      return { previousInterviewData }
    },
    onError: (_error, _partId, context) => {
      if (context?.previousInterviewData) {
        queryClient.setQueryData(
          [INTERVIEW_KEY, interviewId],
          context.previousInterviewData,
        )
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [INTERVIEW_KEY, interviewId],
      })
    },
  })
}

export const useUpdateSummaryPartScore = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      partId,
      score,
    }: {
      partId: string
      score: number | null
    }) => {
      return await api.updateSummaryPartScore(partId, { score })
    },

    // Optimistic update
    onMutate: async ({ partId, score }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: [INTERVIEW_KEY] })

      // Snapshot previous values
      const previousInterviews = new Map()
      queryClient
        .getQueriesData<GetInterviewResponse['interview']>({
          queryKey: [INTERVIEW_KEY],
        })
        .forEach(([key, data]) => {
          previousInterviews.set(key, data)
        })

      // Optimistically update all interview queries
      queryClient.setQueriesData<GetInterviewResponse['interview']>(
        { queryKey: [INTERVIEW_KEY] },
        (old) => {
          if (!old?.SummaryParts) return old

          return {
            ...old,
            SummaryParts: old.SummaryParts.map((part) =>
              part.id === partId ? { ...part, score } : part,
            ),
          }
        },
      )

      return { previousInterviews }
    },

    // Rollback on error
    onError: (_error, _variables, context) => {
      if (context?.previousInterviews) {
        context.previousInterviews.forEach((data, key) => {
          queryClient.setQueryData(key, data)
        })
      }
    },

    // Refetch on success
    onSuccess: (response: UpdateSummaryPartScoreResponse) => {
      const interviewId = response.summaryPart.interviewId
      if (interviewId) {
        queryClient.invalidateQueries({
          queryKey: [INTERVIEW_KEY, interviewId],
        })
      }
    },
  })
}

export const useArchiveCustomSummaryPrompt = () => {
  const queryClient = useQueryClient()
  const interviewId = useInterview().data?.id

  return useMutation({
    mutationFn: async (promptId: string) => {
      await api.archiveCustomSummaryPrompt(promptId)
    },
    onMutate: async (promptId: string) => {
      await queryClient.cancelQueries({
        queryKey: ['list-last-custom-summary-prompts'],
      })
      await queryClient.cancelQueries({
        queryKey: ['list-team-custom-summary-prompts'],
      })
      await queryClient.cancelQueries({
        queryKey: [INTERVIEW_KEY, interviewId],
      })

      const previousInterviewData = queryClient.getQueryData<
        GetInterviewResponse['interview']
      >([INTERVIEW_KEY, interviewId])

      const previousLastPromptsData = queryClient.getQueryData<
        InfiniteData<GetCustomSummaryPromptsResponse>
      >(['list-last-custom-summary-prompts'])

      const previousTeamPromptsData = queryClient.getQueryData<
        InfiniteData<GetTeamCustomSummaryPromptsResponse>
      >(['list-team-custom-summary-prompts'])

      queryClient.setQueryData(
        [INTERVIEW_KEY, interviewId],
        (oldData?: GetInterviewResponse['interview']) => {
          if (!oldData?.Role?.CustomSummaryPartPrompt) return oldData

          const newCustomPrompts = oldData.Role.CustomSummaryPartPrompt.filter(
            (prompt) => prompt.id !== promptId,
          )

          return {
            ...oldData,
            Role: {
              ...oldData.Role,
              CustomSummaryPartPrompt: newCustomPrompts,
            },
          }
        },
      )

      queryClient.setQueryData<InfiniteData<GetCustomSummaryPromptsResponse>>(
        ['list-last-custom-summary-prompts'],
        (oldData) => {
          if (!oldData?.pages) return oldData

          return {
            ...oldData,
            pages: oldData.pages.map((page) => ({
              ...page,
              customSummaryPrompts: page.customSummaryPrompts.filter(
                (prompt) => prompt.id !== promptId,
              ),
            })),
          }
        },
      )

      queryClient.setQueryData<
        InfiniteData<GetTeamCustomSummaryPromptsResponse>
      >(['list-team-custom-summary-prompts'], (oldData) => {
        if (!oldData?.pages) return oldData

        return {
          ...oldData,
          pages: oldData.pages.map((page) => ({
            ...page,
            customSummaryPrompts: page.customSummaryPrompts.filter(
              (prompt) => prompt.id !== promptId,
            ),
          })),
        }
      })

      return {
        previousInterviewData,
        previousLastPromptsData,
        previousTeamPromptsData,
      }
    },
    onError: (_error, _promptId, context) => {
      if (context?.previousInterviewData) {
        queryClient.setQueryData(
          [INTERVIEW_KEY, interviewId],
          context.previousInterviewData,
        )
      }
      if (context?.previousLastPromptsData) {
        queryClient.setQueryData(
          ['list-last-custom-summary-prompts'],
          context.previousLastPromptsData,
        )
      }
      if (context?.previousTeamPromptsData) {
        queryClient.setQueryData(
          ['list-team-custom-summary-prompts'],
          context.previousTeamPromptsData,
        )
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['list-last-custom-summary-prompts'],
      })
      queryClient.invalidateQueries({
        queryKey: ['list-team-custom-summary-prompts'],
      })
    },
  })
}

export const useShortInterviewsList = (
  filters: GetShortInterviewsRequestParams,
) => {
  return useInfiniteQuery({
    queryKey: [SHORT_INTERVIEWS_LIST_KEY, filters],
    queryFn: async ({ pageParam = 1 }) => {
      const data = await api.getShortInterviews({
        page: pageParam,
        limit: 20,
        ...filters,
      })
      const shouldSkipLocalRecordingCheck =
        import.meta.env.MODE === 'e2e' ||
        import.meta.env.VITE_E2E_AUTH_BYPASS === 'true'
      if (!shouldSkipLocalRecordingCheck) {
        for (const interview of data.data.interviews) {
          const localRecording = await getAudioChunks(interview.id)
          // if recording is older than 14 days -- delete it
          if (
            localRecording?.recordingStartTime &&
            localRecording.recordingStartTime <
              Date.now() - 14 * 24 * 60 * 60 * 1000
          ) {
            await deleteAudioChunks(interview.id)
            continue
          }
          interview.hasLocalRecording = !!localRecording
        }
      }
      return data.data
    },
    getNextPageParam: (lastPage, pages) => {
      return lastPage.hasMore ? pages.length + 1 : undefined
    },
    initialPageParam: 1,
    staleTime: 1000 * 60, // 1 minute
    gcTime: 1000 * 60 * 5, // Keep in cache for 5 minutes
  })
}

export const useInterviewFilterOptions = () => {
  return useQuery<GetInterviewFilterOptionsResponse>({
    queryKey: [INTERVIEW_FILTER_OPTIONS_KEY],
    queryFn: async () => {
      const data = await api.getInterviewFilterOptions({ type: 'NORMAL' })
      const uniqueJobTitles = new Set(
        data.data.jobTitles.map((jobTitle) => jobTitle),
      )
      const uniqueInterviewers = new Set(
        data.data.interviewers.map((interviewer) => interviewer),
      )

      return {
        jobTitles: Array.from(uniqueJobTitles),
        interviewers: Array.from(uniqueInterviewers),
        decisions: data.data.decisions,
        hasEvents: data.data.hasEvents,
      }
    },
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  })
}

export const useIntakeFilterOptions = () => {
  return useQuery<GetInterviewFilterOptionsResponse>({
    queryKey: [INTERVIEW_FILTER_OPTIONS_INTAKE_KEY],
    queryFn: async () => {
      const data = await api.getInterviewFilterOptions({ type: 'INTAKE' })

      const uniqueJobTitles = new Set(
        data.data.jobTitles.map((jobTitle) => jobTitle),
      )
      const uniqueInterviewers = new Set(
        data.data.interviewers.map((interviewer) => interviewer),
      )

      return {
        jobTitles: Array.from(uniqueJobTitles),
        interviewers: Array.from(uniqueInterviewers),
        decisions: data.data.decisions,
        hasEvents: data.data.hasEvents,
      }
    },
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  })
}
